import { TitleCasePipe } from "@angular/common";
import { Component } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  AlertController,
  IonicPage,
  LoadingController,
  ModalController,
  NavController,
  NavParams,
} from "ionic-angular";
import {
  CompanyWorkGroupWithPrivilege,
  WorkgroupPost,
  WorkgroupPrivilege,
} from "models/users/user-workgroups";
import { Subscription } from "rxjs";
import { AccessControlService } from "../../../../app/access-control/access-control-service";
import { AuthService } from "../../../../app/auth/auth.service";
import CompanyDetails from "../../../../models/company-details/company-details";
import { CompanyManagementService } from "../../../../providers/company-management-service/company-management-service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import { UsersService } from "../../../../providers/users-service/users.service";
import { CompanyPage } from "../../company";
import { WorkgroupAddMember } from "../add-member/workgroup-add-member";

@IonicPage({
  segment: "company/company-management/work-group-permissions",
})
@Component({
  selector: "workgroup-permission-details",
  templateUrl: "workgroup-permission-details.html",
})
export class WorkgroupPermissionDetail extends CompanyPage {
  workgroupPrivilege: CompanyWorkGroupWithPrivilege;
  workgroupId: number;
  workgroupName: string;
  tenantId: number;
  workgroupData: CompanyDetails = null;
  permissionGroups: any = [];

  tabs = [
    {
      id: "details",
      title: "Details",
    },
  ];

  activeTab = this.tabs[0];
  parentForm: FormGroup;
  _isEditMode: boolean = false;
  isEditName: boolean = true;
  subscriptions: Subscription[] = [];
  totalPages = 0;
  currentPage = 0;
  private spinnerCounter: number = 0;
  private spinner: any = null;

  get isEditMode() {
    return this._isEditMode;
  }

  set isEditMode(value: boolean) {
    this._isEditMode = value;
  }

  constructor(
    protected accessControlService: AccessControlService,
    protected authSvc: AuthService,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected loader: LoadingController,
    protected userService: UsersService,
    protected stateManagementService: StateManagementService,
    protected companyManagementService: CompanyManagementService,
    protected formBuilder: FormBuilder,
    protected alertController: AlertController,
    protected modalController: ModalController,
    protected fb: FormBuilder,
    protected titleCasePipe: TitleCasePipe
  ) {
    super(
      accessControlService,
      navCtrl,
      navParams,
      userService,
      stateManagementService,
      authSvc
    );
    this.workgroupId = parseInt(this.navParams.get("workgroupId"));
    this.workgroupName = this.navParams.get("workgroupName");
    this.isEditMode = this.navParams.get("isEditMode");
    this.tenantId = parseInt(this.navParams.get("tenantId"));

    this.getWorkgroup();

    this.parentForm = this.fb.group({
      id: [""],
      generalDetails: [],
      billing: [],
      company: [],
      compliance: [],
      patient: [],
      providers: [],
      reports: [],
    });

    this.parentForm.patchValue({
      generalDetails: { groupName: this.workgroupName },
    });

    this.subscriptions.push(
      this.parentForm.valueChanges.subscribe((value) => {
        // generalDetails is null until its child registers, and any bucket child
        // emitting first drives this handler with it still null.
        if (value && value.generalDetails && value.generalDetails.groupName) {
          this.workgroupName = value.generalDetails.groupName;
        } else {
          this.workgroupName = "";
        }
      })
    );
  }

  addMember() {
    // No increase/decreaseSpinnerCount pair here: creating a modal is synchronous,
    // so the old pair flashed the blocking overlay on and off for zero benefit.
    let modal = this.modalController.create(WorkgroupAddMember, {});
    modal
      .present()
      .then(() => {})
      .catch((err) => {
        console.error(err);
      });
    modal.onDidDismiss(() => {
      // Refresh the member list here
    });
  }

  navigateBack() {
    this.navCtrl.pop();
    this.stateManagementService.setSectionId("Workgroup Management");
  }

  setActiveTab(tab) {
    this.activeTab = tab;
    if (this.activeTab.id === "permissions") {
      this.getWorkgroup();
    }
  }

  toggleEditMode() {
    this.isEditMode = !this.isEditMode;
  }

  toggleEditName() {
    this.isEditName = false;
  }

  mapArrayToWorkgroupPrivilege(permissions: any): WorkgroupPrivilege[] {
    let a: WorkgroupPrivilege[] = [];
    for (let i = 0; i < permissions.length; i++) {
      let p = permissions[i];
      let access = 0;

      if (p.add) {
        access += 1;
      }

      if (p.view) {
        access += 2;
      }

      if (p.edit) {
        access += 4;
      }

      if (p.delete) {
        access += 8;
      }

      a.push({
        domainName: p.domainName,
        resourceName: "",
        privilegeAccess: access,
        domainId: p.domainId,
      });
    }

    return a;
  }

  /**
   * A bucket's form value stays `null` until its <workgroup-permissions> child has
   * registered a `permissions` FormArray, and the child only does that from
   * ngOnChanges when the bucket arrives NON-EMPTY (`currentValue.length > 0`). So a
   * tenant without a `reports` bucket — or a Save pressed before the GET resolves —
   * left the control null and `bucket.permissions` threw a TypeError, aborting
   * saveData() before any request went out. The user saw a spinner and no error, and
   * their permission edits were silently discarded (CO-3351).
   */
  private bucketPermissions(name: string): WorkgroupPrivilege[] {
    const control = this.parentForm.get(name);
    const value = control ? control.value : null;
    if (!value || !value.permissions) {
      return [];
    }
    return this.mapArrayToWorkgroupPrivilege(value.permissions);
  }

  saveData() {
    // Order is load-bearing for nothing functionally, but kept as it shipped.
    let permissions: WorkgroupPrivilege[] = [];
    let fullPermissions = permissions.concat(
      this.bucketPermissions("billing"),
      this.bucketPermissions("company"),
      this.bucketPermissions("compliance"),
      this.bucketPermissions("patient"),
      this.bucketPermissions("reports"),
      this.bucketPermissions("providers")
    );
    const generalDetails = this.parentForm.get("generalDetails").value;
    let groupName = generalDetails
      ? generalDetails.groupName
      : this.workgroupName;
    const tenantId = this.resolveTenantId();
    let post: WorkgroupPost = {
      // groupId: this.authSvc.getUserOrganization(),
      groupId: tenantId,
      groupName: groupName,
      permissions: fullPermissions,
    };

    this.increaseSpinnerCount(`Saving "${this.workgroupName}" Work Group ...`);

    if (isNaN(this.workgroupId)) {
      this.companyManagementService
        .createCompanyWorkgroup(tenantId, post)
        .subscribe(
          () => {
            this.decreaseSpinnerCount();
            this.navCtrl.pop();
          },
          (err) => {
            console.error("Error saving work group permissions. ", err);
            this.decreaseSpinnerCount();
          }
        );
    } else {
      this.companyManagementService
        .updateCompanyWorkgroup(tenantId, this.workgroupId, post)
        .subscribe(
          () => {
            this.decreaseSpinnerCount();
            this.navCtrl.pop();
          },
          (err) => {
            console.error("Error saving work group permissions. ", err);
            this.decreaseSpinnerCount();
          }
        );
    }
  }

  /**
   * The tenant this editor both READS and WRITES. The explicit navParam wins (a
   * company admin opens the editor for a specific tenant); the signed-in tenant is
   * the fallback.
   *
   * Previously the two disagreed: getWorkgroup() read `authSvc.getUser().tenantId`
   * while saveData() wrote to the navParam `tenantId`. When those differ the editor
   * showed one tenant's privilege matrix and PUT it onto another — an RBAC
   * corruption. And with the navParam absent, `parseInt(undefined)` produced NaN,
   * so the save URL contained a literal "NaN" (CO-3351).
   */
  private resolveTenantId(): number {
    if (!isNaN(this.tenantId) && this.tenantId > 0) {
      return this.tenantId;
    }
    const user = this.authSvc.getUser();
    return user ? user.tenantId : null;
  }

  /**
   * Rebuild the tab strip from the response. Rebuilding (rather than appending) is
   * the fix for setActiveTab() calling getWorkgroup() again: each call used to push
   * another copy of every bucket key, so the strip grew "Details | Billing | Company
   * | Billing | Company | ..." on every revisit (CO-3351).
   */
  private applyWorkgroup(response: CompanyWorkGroupWithPrivilege) {
    this.tabs.length = 1; // keep the base "Details" tab, drop any previous buckets
    for (const key in response.buckets) {
      this.tabs.push({
        id: key,
        title: this.titleCasePipe.transform(key),
      });
    }
    // The previously-active bucket may no longer exist in this response.
    if (!this.tabs.some((t) => t.id === this.activeTab.id)) {
      this.activeTab = this.tabs[0];
    }
    this.workgroupPrivilege = response;
    this.decreaseSpinnerCount();
  }

  getWorkgroup() {
    this.increaseSpinnerCount(`Loading "${this.workgroupName}" Work Group...`);
    const tenantId = this.resolveTenantId();
    const onError = (err) => {
      console.error("Error loading work group permissions. ", err);
      this.decreaseSpinnerCount();
    };

    if (this.workgroupId && this.workgroupId > 0) {
      this.companyManagementService
        .getWorkGroupById(
          // this.authSvc.getUserOrganization(),
          tenantId,
          this.workgroupId
        )
        .subscribe(
          (response: CompanyWorkGroupWithPrivilege) =>
            this.applyWorkgroup(response),
          onError
        );
    } else {
      this.companyManagementService
        .getWorkGroupPrivileges(tenantId)
        .subscribe(
          (response: CompanyWorkGroupWithPrivilege) =>
            this.applyWorkgroup(response),
          onError
        );
    }
  }

  mapObjectFromForm() {
    // TODO: Reformat the form to submit ?
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Loading ...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && !this.spinner) {
      this.spinner = this.loader.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  get billingPermissions(): WorkgroupPrivilege[] {
    if (
      this.workgroupPrivilege &&
      this.workgroupPrivilege.buckets &&
      this.workgroupPrivilege.buckets["billing"]
    ) {
      return this.workgroupPrivilege.buckets["billing"].slice().sort(
        this.sortedPermissions
      );
    }

    return [];
  }

  get companyPermissions(): WorkgroupPrivilege[] {
    if (
      this.workgroupPrivilege &&
      this.workgroupPrivilege.buckets &&
      this.workgroupPrivilege.buckets["company"]
    ) {
      return this.workgroupPrivilege.buckets["company"].slice().sort(
        this.sortedPermissions
      );
    }

    return [];
  }

  get compliancePermissions(): WorkgroupPrivilege[] {
    if (
      this.workgroupPrivilege &&
      this.workgroupPrivilege.buckets &&
      this.workgroupPrivilege.buckets["compliance"]
    ) {
      return this.workgroupPrivilege.buckets["compliance"].slice().sort(
        this.sortedPermissions
      );
    }

    return [];
  }

  get patientsPermissions(): WorkgroupPrivilege[] {
    if (
      this.workgroupPrivilege &&
      this.workgroupPrivilege.buckets &&
      this.workgroupPrivilege.buckets["patient"]
    ) {
      return this.workgroupPrivilege.buckets["patient"].slice().sort(
        this.sortedPermissions
      );
    }

    return [];
  }

  get providersPermissions(): WorkgroupPrivilege[] {
    if (
      this.workgroupPrivilege &&
      this.workgroupPrivilege.buckets &&
      this.workgroupPrivilege.buckets["providers"]
    ) {
      return this.workgroupPrivilege.buckets["providers"].slice().sort(
        this.sortedPermissions
      );
    }

    return [];
  }

  get reportsPermissions(): WorkgroupPrivilege[] {
    if (
      this.workgroupPrivilege &&
      this.workgroupPrivilege.buckets &&
      this.workgroupPrivilege.buckets["reports"]
    ) {
      return this.workgroupPrivilege.buckets["reports"].slice().sort(
        this.sortedPermissions
      );
    }

    return [];
  }

  sortedPermissions(a: WorkgroupPrivilege, b: WorkgroupPrivilege): number {
    var nameA = a.domainName.toUpperCase(); // ignore upper and lowercase
    var nameB = b.domainName.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    // Equal names fell off the end returning undefined. Array.prototype.sort
    // coerces that to NaN, which engines are free to treat as any ordering — so
    // equal-named domains could swap positions between renders (CO-3351).
    return 0;
  }

  ngOnDestroy() {
    // This page had NO ngOnDestroy at all, so neither the parentForm.valueChanges
    // subscription nor any of BasePage's eight feature-toggle subscriptions were
    // ever released — every visit to the permission editor leaked all nine
    // (CO-3351).
    super.ngOnDestroy();
    this.subscriptions.forEach((val) => val.unsubscribe());
    this.subscriptions = [];
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/company-details/workgroup-permission-details/workgroup-permission-details.ts